// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========

import type { RemoteCommandProjection } from '@web/api/remoteControl';

const COMMAND_HISTORY_PREFIX = 'eigent:remote-control-command-history:v1:';
const COMMAND_HISTORY_LIMIT = 32;
const COMMAND_HISTORY_TTL_MS = 24 * 60 * 60 * 1000;
const TERMINAL_COMMAND_STATUSES = new Set([
  'completed',
  'failed',
  'rejected',
  'expired',
  'outcome_unknown',
]);
export const INTERACTION_DECISION_PENDING_TIMEOUT_MS = 2 * 60 * 1000;
export const COMMAND_RECONCILE_MAX_BACKOFF_MS = 60 * 1000;

export function commandReconcileNextAttemptAt(
  attempts: number,
  now = Date.now()
): number {
  return (
    now + Math.min(COMMAND_RECONCILE_MAX_BACKOFF_MS, 1_000 * 2 ** attempts)
  );
}

export function scheduleCommandReconciliation(
  inFlight: Promise<void> | null,
  force: boolean,
  run: () => Promise<void>
): Promise<void> {
  if (!inFlight) return run();
  return force ? inFlight.then(run) : inFlight;
}

export type RemoteCommandDisplay = {
  id: string;
  projectId?: string | null;
  runId?: string | null;
  clientRequestId?: string | null;
  humanInteractionId?: string | null;
  content: string;
  type: string;
  status: string;
  error?: string;
  lateResult?: boolean;
  lastSyncedAt?: string;
  projection?: RemoteCommandProjection;
  interactionId?: string;
  interactionRequest?: Record<string, unknown>;
  interactionTarget?: {
    project_id: string | null;
    task_id: string | null;
    brain_session_id: string | null;
  } | null;
  requestPayload?: Record<string, unknown>;
  requestTarget?: {
    project_id: string | null;
    task_id: string | null;
    brain_session_id: string | null;
  } | null;
  createdAt?: number;
};

/**
 * Return the strongest identity available for correlating a displayed command
 * with a projected chat run. Canonical run ids take precedence, followed by
 * the client-generated idempotency key. The command id fallback preserves the
 * legacy command-status reconciliation path.
 */
export function getRemoteCommandCorrelationId(
  command: Pick<RemoteCommandDisplay, 'id' | 'runId' | 'clientRequestId'>
): string {
  return command.runId || command.clientRequestId || command.id;
}

const RETRYABLE_INTERACTION_COMMAND_STATUSES = new Set([
  'failed',
  'rejected',
  'outcome_unknown',
  'expired',
]);
const COMMAND_STATUS_RANK: Record<string, number> = {
  pending: 0,
  leased: 1,
  sent: 1,
  delivered: 1,
  confirmed: 2,
  durably_received: 2,
  accepted: 3,
  running: 4,
  rejected: 5,
  completed: 5,
  failed: 5,
  outcome_unknown: 5,
  expired: 5,
};

export function durablyResolvedInteractionIds(
  commands: RemoteCommandDisplay[]
): ReadonlySet<string> {
  return new Set(
    commands
      .filter(
        (command) =>
          command.type === 'interaction_decision' &&
          command.status === 'completed' &&
          command.interactionId
      )
      .map((command) => command.interactionId as string)
  );
}

export function durablyResolvedHumanReplyIds(
  commands: RemoteCommandDisplay[]
): ReadonlySet<string> {
  return new Set(
    commands
      .filter(
        (command) =>
          command.type === 'human_reply' &&
          command.status === 'completed' &&
          command.humanInteractionId
      )
      .map((command) => command.humanInteractionId as string)
  );
}

export function interactionDecisionIsPending(
  commands: RemoteCommandDisplay[],
  interactionId: string,
  now = Date.now(),
  expectedVersion?: number,
  actionDigest?: string | null
): boolean {
  const command = commands
    .slice()
    .reverse()
    .find(
      (item) =>
        item.type === 'interaction_decision' &&
        item.interactionId === interactionId &&
        commandMatchesInteractionGeneration(item, expectedVersion, actionDigest)
    );
  if (
    !command ||
    command.status === 'completed' ||
    RETRYABLE_INTERACTION_COMMAND_STATUSES.has(command.status)
  ) {
    return false;
  }
  return (
    command.createdAt === undefined ||
    now - command.createdAt < INTERACTION_DECISION_PENDING_TIMEOUT_MS
  );
}

export function interactionDecisionTimedOut(
  commands: RemoteCommandDisplay[],
  interactionId: string,
  now = Date.now(),
  expectedVersion?: number,
  actionDigest?: string | null
): boolean {
  const command = commands
    .slice()
    .reverse()
    .find(
      (item) =>
        item.type === 'interaction_decision' &&
        item.interactionId === interactionId &&
        commandMatchesInteractionGeneration(item, expectedVersion, actionDigest)
    );
  return Boolean(
    command &&
    command.createdAt !== undefined &&
    command.status !== 'completed' &&
    !RETRYABLE_INTERACTION_COMMAND_STATUSES.has(command.status) &&
    now - command.createdAt >= INTERACTION_DECISION_PENDING_TIMEOUT_MS
  );
}

export function latestInteractionDecisionCommand(
  commands: RemoteCommandDisplay[],
  interactionId: string,
  expectedVersion?: number,
  actionDigest?: string | null
): RemoteCommandDisplay | null {
  return (
    commands
      .slice()
      .reverse()
      .find(
        (item) =>
          item.type === 'interaction_decision' &&
          item.interactionId === interactionId &&
          commandMatchesInteractionGeneration(
            item,
            expectedVersion,
            actionDigest
          )
      ) || null
  );
}

export function interactionDecisionTerminalProblem(
  commands: RemoteCommandDisplay[],
  interactionId: string,
  expectedVersion?: number,
  actionDigest?: string | null
): string | null {
  const command = latestInteractionDecisionCommand(
    commands,
    interactionId,
    expectedVersion,
    actionDigest
  );
  return command && RETRYABLE_INTERACTION_COMMAND_STATUSES.has(command.status)
    ? command.status
    : null;
}

export function latestSupersedableInteractionDecisionCommand(
  commands: RemoteCommandDisplay[],
  interactionId: string
): RemoteCommandDisplay | null {
  const command = latestInteractionDecisionCommand(commands, interactionId);
  return command && RETRYABLE_INTERACTION_COMMAND_STATUSES.has(command.status)
    ? command
    : null;
}

function commandMatchesInteractionGeneration(
  command: RemoteCommandDisplay,
  expectedVersion?: number,
  actionDigest?: string | null
): boolean {
  if (expectedVersion === undefined) return true;
  const request = command.interactionRequest;
  if (!request) return false;
  return (
    Number(request.expected_version) === expectedVersion &&
    (request.action_digest ?? null) === (actionDigest ?? null)
  );
}

export function watermarkMatchesProject(
  payloadProjectId: string | null | undefined,
  targetProjectId: string | null | undefined
): boolean {
  return (payloadProjectId ?? null) === (targetProjectId ?? null);
}

function commandHistoryKey(sessionId: string): string {
  return `${COMMAND_HISTORY_PREFIX}${sessionId}`;
}

function isOptionalString(value: unknown): value is string | null | undefined {
  return value == null || typeof value === 'string';
}

function validStoredCommand(value: unknown): value is RemoteCommandDisplay {
  if (!value || typeof value !== 'object') return false;
  const command = value as Partial<RemoteCommandDisplay>;
  return (
    typeof command.id === 'string' &&
    typeof command.content === 'string' &&
    typeof command.type === 'string' &&
    typeof command.status === 'string' &&
    isOptionalString(command.projectId) &&
    isOptionalString(command.runId) &&
    isOptionalString(command.clientRequestId) &&
    isOptionalString(command.humanInteractionId) &&
    typeof command.createdAt === 'number'
  );
}

export function loadRemoteCommandHistory(
  sessionId: string
): RemoteCommandDisplay[] {
  const now = Date.now();
  try {
    const parsed = JSON.parse(
      window.sessionStorage.getItem(commandHistoryKey(sessionId)) || '[]'
    );
    return Array.isArray(parsed)
      ? parsed
          .filter(
            (command): command is RemoteCommandDisplay =>
              validStoredCommand(command) &&
              now - (command.createdAt || 0) <= COMMAND_HISTORY_TTL_MS
          )
          .slice(-COMMAND_HISTORY_LIMIT)
      : [];
  } catch {
    return [];
  }
}

export function saveRemoteCommandHistory(
  sessionId: string,
  commands: RemoteCommandDisplay[]
): void {
  try {
    window.sessionStorage.setItem(
      commandHistoryKey(sessionId),
      JSON.stringify(commands.slice(-COMMAND_HISTORY_LIMIT))
    );
  } catch {
    // Command delivery remains functional when browser storage is unavailable;
    // only refresh/reconnect reconciliation becomes less complete.
  }
}

export function commandProjectionStatus(
  projection: RemoteCommandProjection
): string {
  if (projection.receipt_state === 'expired') return 'expired';
  if (projection.execution_state !== 'not_started') {
    return projection.execution_state;
  }
  if (projection.admission_state !== 'unknown') {
    return projection.admission_state;
  }
  return projection.receipt_state;
}

/**
 * Merge delivery from the legacy command-status lane and the canonical
 * three-dimensional projection lane. Once a canonical projection has been
 * observed, a delayed legacy frame may enrich display fields but must not
 * regress its status.
 */
export function mergeRemoteCommand(
  current: RemoteCommandDisplay[],
  incoming: RemoteCommandDisplay
): RemoteCommandDisplay[] {
  const index = current.findIndex(
    (command) =>
      (Boolean(incoming.clientRequestId) &&
        command.clientRequestId === incoming.clientRequestId) ||
      command.id === incoming.id
  );
  if (index < 0) {
    return [
      ...current,
      { ...incoming, createdAt: incoming.createdAt ?? Date.now() },
    ];
  }

  const existing = current[index];
  const terminal = new Set([
    'rejected',
    'completed',
    'failed',
    'outcome_unknown',
    'expired',
  ]);
  const existingRank = COMMAND_STATUS_RANK[existing.status];
  const incomingRank = COMMAND_STATUS_RANK[incoming.status];
  const preserveProgress =
    (terminal.has(existing.status) && existing.status !== incoming.status) ||
    (existingRank !== undefined &&
      incomingRank !== undefined &&
      incomingRank < existingRank);
  const preserveCanonical = Boolean(
    existing.projection && (!incoming.projection || preserveProgress)
  );
  const preserveTerminal =
    TERMINAL_COMMAND_STATUSES.has(existing.status) &&
    !TERMINAL_COMMAND_STATUSES.has(incoming.status);
  const merged: RemoteCommandDisplay = {
    ...existing,
    ...incoming,
    content:
      incoming.content === 'Remote command' &&
      existing.content !== 'Remote command'
        ? existing.content
        : incoming.content,
    type:
      incoming.type === 'unknown' && existing.type !== 'unknown'
        ? existing.type
        : incoming.type,
    status:
      preserveCanonical || preserveTerminal || preserveProgress
        ? existing.status
        : incoming.status,
    error: incoming.error ?? existing.error,
    lateResult: incoming.lateResult ?? existing.lateResult,
    lastSyncedAt: incoming.lastSyncedAt ?? existing.lastSyncedAt,
    projection: incoming.projection ?? existing.projection,
    projectId: incoming.projectId ?? existing.projectId,
    runId: incoming.runId ?? existing.runId,
    clientRequestId: incoming.clientRequestId ?? existing.clientRequestId,
    humanInteractionId:
      incoming.humanInteractionId ?? existing.humanInteractionId,
    interactionId: incoming.interactionId ?? existing.interactionId,
    interactionRequest:
      incoming.interactionRequest ?? existing.interactionRequest,
    interactionTarget: incoming.interactionTarget ?? existing.interactionTarget,
    requestPayload: incoming.requestPayload ?? existing.requestPayload,
    requestTarget: incoming.requestTarget ?? existing.requestTarget,
    createdAt: existing.createdAt ?? incoming.createdAt ?? Date.now(),
  };
  if (preserveCanonical) merged.projection = existing.projection;
  return current.map((command, commandIndex) =>
    commandIndex === index ? merged : command
  );
}
